import { useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Play, X } from "lucide-react";
import {
    STAFF_GROUPS,
    staffFace,
    staffPoster,
    staffVideo,
} from "@/lib/staff";

function FaceCard({ person, onOpen }) {
    const playable = person.video;
    const face = (
        <img
            src={staffFace(person)}
            alt=""
            width={192}
            height={192}
            loading="lazy"
            className="h-28 w-28 sm:h-32 sm:w-32 rounded-full object-cover ring-2 ring-white/80 bg-slate-800"
        />
    );
    const caption = (
        <>
            <span
                className="mt-4 block text-white text-lg font-semibold leading-snug"
                data-testid={`staff-name-${person.id}`}
            >
                {person.name}
            </span>
            <span
                className="mt-1 block text-slate-300 text-base leading-snug"
                data-testid={`staff-role-${person.id}`}
            >
                {person.role}
            </span>
        </>
    );

    if (!playable) {
        return (
            <li
                className="flex flex-col items-center border border-white/10 bg-[color-mix(in_srgb,#7dd3fc_10%,#000)] px-4 py-6 text-center"
                data-testid={`staff-card-${person.id}`}
            >
                <span className="opacity-90">{face}</span>
                {caption}
                <span
                    className="fsrs-mono mt-3 inline-block border border-white/20 px-2 py-1 text-[13px] tracking-[0.14em] text-slate-300"
                    data-testid={`staff-soon-${person.id}`}
                >
                    SOUND-OFF COMING SOON
                </span>
            </li>
        );
    }

    return (
        <li className="flex" data-testid={`staff-card-${person.id}`}>
            <button
                type="button"
                onClick={(e) => onOpen(person, e.currentTarget)}
                aria-haspopup="dialog"
                aria-label={`Play ${person.name}'s intro`}
                className="group flex w-full cursor-pointer flex-col items-center border border-white/10 bg-[color-mix(in_srgb,#7dd3fc_10%,#000)] px-4 py-6 text-center transition-colors hover:border-[#7DD3FC] focus:outline-none focus-visible:border-[#7DD3FC] focus-visible:ring-2 focus-visible:ring-[#7DD3FC]"
                data-testid={`staff-play-${person.id}`}
            >
                <span className="relative">
                    {face}
                    <span
                        aria-hidden="true"
                        className="absolute bottom-0 right-0 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#7DD3FC] text-slate-900 shadow-lg transition-transform group-hover:scale-110"
                    >
                        <Play className="h-2 w-2" fill="currentColor" />
                    </span>
                </span>
                {caption}
            </button>
        </li>
    );
}

export default function StaffGallery() {
    const [active, setActive] = useState(null);
    const triggerRef = useRef(null);

    const open = (person, trigger) => {
        triggerRef.current = trigger;
        setActive(person);
    };

    return (
        <>
            {STAFF_GROUPS.map((group) => (
                <section
                    key={group.id}
                    className="border-b border-white/10 bg-[color-mix(in_srgb,#7dd3fc_8%,#000)]"
                    aria-labelledby={`staff-group-${group.id}`}
                    data-testid={`staff-group-${group.id}`}
                >
                    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12 lg:py-16">
                        <h2
                            id={`staff-group-${group.id}`}
                            className="fsrs-overline mb-8"
                        >
                            /&nbsp;{group.label}
                        </h2>
                        <ul
                            className={
                                "grid grid-cols-1 min-[420px]:grid-cols-2 gap-4 sm:gap-6 " +
                                (group.id === "team"
                                    ? "md:grid-cols-3 lg:grid-cols-4"
                                    : group.people.length === 2
                                      ? "md:grid-cols-2 lg:max-w-[720px]"
                                      : "md:grid-cols-4")
                            }
                        >
                            {group.people.map((p) => (
                                <FaceCard key={p.id} person={p} onOpen={open} />
                            ))}
                        </ul>
                    </div>
                </section>
            ))}

            <DialogPrimitive.Root
                open={!!active}
                onOpenChange={(o) => {
                    if (!o) setActive(null);
                }}
            >
                <DialogPrimitive.Portal>
                    <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/85" />
                    <DialogPrimitive.Content
                        className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-1.5rem)] max-w-[720px] max-h-[calc(100dvh-1.5rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto border border-[#7DD3FC]/40 bg-black p-4 sm:p-6 text-white shadow-2xl focus:outline-none"
                        data-testid="staff-modal"
                        onCloseAutoFocus={(e) => {
                            // Return focus to the face that opened the dialog.
                            if (triggerRef.current) {
                                e.preventDefault();
                                triggerRef.current.focus({ preventScroll: true });
                            }
                        }}
                    >
                        {active && (
                            <>
                                <div className="pr-12 mb-4">
                                    <DialogPrimitive.Title
                                        className="fsrs-heading text-2xl leading-tight"
                                        data-testid="staff-modal-title"
                                    >
                                        {active.name}
                                    </DialogPrimitive.Title>
                                    <DialogPrimitive.Description className="mt-1 text-slate-300 text-base">
                                        {active.role}
                                    </DialogPrimitive.Description>
                                </div>
                                <video
                                    key={active.id}
                                    className="block w-full aspect-video bg-black"
                                    src={staffVideo(active)}
                                    poster={staffPoster(active)}
                                    controls
                                    autoPlay
                                    playsInline
                                    preload="auto"
                                    aria-label={`${active.name} intro video`}
                                    data-testid="staff-modal-video"
                                />
                                <DialogPrimitive.Close
                                    className="absolute right-2 top-2 flex h-11 w-11 cursor-pointer items-center justify-center text-slate-200 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7DD3FC]"
                                    data-testid="staff-modal-close"
                                >
                                    <X className="h-6 w-6" aria-hidden="true" />
                                    <span className="sr-only">Close video</span>
                                </DialogPrimitive.Close>
                            </>
                        )}
                    </DialogPrimitive.Content>
                </DialogPrimitive.Portal>
            </DialogPrimitive.Root>
        </>
    );
}
